import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import {
  getPassportFlags,
  isPassportEnabled,
  joinPassportCampaign,
  listPassportCampaigns,
  type PassportCampaignSummary,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PASSPORT_DISABLED_DESCRIPTION, PASSPORT_DISABLED_TITLE, formatEncounterDate } from './passportCopy';
import { PassportChrome } from './PassportChrome';

const PassportCampaignsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<PassportCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<number | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/passport/campaigns' }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listPassportCampaigns();
      setCampaigns(data);
      void apiService.trackEvent('passport_campaign_viewed', { metadata: { view: 'list' } });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags)) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  const handleJoin = async (campaign: PassportCampaignSummary, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setJoiningId(campaign.id);
    try {
      await joinPassportCampaign({ campaign_id: campaign.id });
      showToast(`Joined ${campaign.title}.`, 'success');
      void apiService.trackEvent('passport_campaign_joined', { metadata: { campaign_id: campaign.id } });
      navigate(`/passport/campaigns/${encodeURIComponent(campaign.slug)}`);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to join campaign', 'error');
    } finally {
      setJoiningId(null);
    }
  };

  if (authLoading || !isAuthenticated || flagsLoading) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags)) {
    return (
      <PassportChrome active="campaigns" flags={flags}>
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PassportChrome>
    );
  }

  const noCampaignModulesEnabled = !flags?.sponsor_campaigns_enabled;

  return (
    <PassportChrome
      active="campaigns"
      flags={flags}
      title={
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            Hunts
          </h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            Real-world waypoints at shows and shops. Scan in person, then see what you have found.
          </p>
        </div>
      }
    >
      {noCampaignModulesEnabled ? (
        <EmptyState
          title="No active hunts"
          description="Event and shop activations will appear here once organizers publish them."
        />
      ) : loading ? (
        <p className="text-zinc-500 text-sm" role="status">
          Loading hunts…
        </p>
      ) : error ? (
        <EmptyState
          title="Couldn't load hunts"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : campaigns.length === 0 ? (
        <EmptyState title="No active hunts" description="Check back closer to your next event." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {campaigns.map((c) => (
            <Link
              key={c.public_id}
              to={`/passport/campaigns/${encodeURIComponent(c.slug)}`}
              className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c] p-5 block transition-colors hover:border-white/[0.14] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60"
            >
              <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                {c.campaign_type.replace(/_/g, ' ')}
              </p>
              <h2 className="text-base font-semibold text-white mb-1">{c.title}</h2>
              {c.description ? <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{c.description}</p> : null}
              <p className="text-xs text-zinc-500 mb-3">
                {c.starts_at ? `Starts ${formatEncounterDate(c.starts_at)}` : ''}
                {c.ends_at ? ` · Ends ${formatEncounterDate(c.ends_at)}` : ''}
              </p>
              {c.participation ? (
                <span className="badge bg-amber-500/10 text-amber-200 border border-amber-500/25 text-xs">
                  {c.participation.objects_visited_count}/{c.participation.required_objects_total} found
                </span>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={joiningId === c.id}
                  onClick={(e) => void handleJoin(c, e)}
                >
                  Join hunt
                </Button>
              )}
            </Link>
          ))}
        </div>
      )}
    </PassportChrome>
  );
};

export default PassportCampaignsPage;
